/**
 * idiomaSesion.js — el idioma que le toca a ESTA sesión del navegador.
 *
 * ── EL PROBLEMA ─────────────────────────────────────────────────────────────
 *
 * Las URLs públicas sin prefijo (`/`, `/pricing`…) son españolas y fuerzan el
 * español (ver `rutasPublicas.js`). Un portugués que entraba por la home lo
 * veía todo en español, pulsaba «Registrarse», el SPA llevaba ese español
 * hasta `/register` y el alta quedaba guardada como `preferredLanguage: 'es'`:
 * correos, IA, informes y factura en español para siempre.
 *
 * ── LO QUE SE DECIDIÓ ───────────────────────────────────────────────────────
 *
 * 1. Con IP de Portugal se FUERZA el portugués al empezar cada sesión. Si el
 *    usuario cambia a mano, se respeta hasta que cierre la sesión.
 * 2. Quien entra con señal portuguesa (esa IP, o el navegador en `pt` sin otra
 *    elección guardada) en una página pública española va a la misma página
 *    en `/pt/`, UNA vez por sesión.
 *
 * «Sesión» es la de `sessionStorage`: una pestaña o un navegador nuevos. El
 * país lo dice el servidor (`GET /api/v1/public/geo/`), porque el navegador
 * no puede saberlo.
 *
 * ── LO QUE NO PUEDE PASAR ───────────────────────────────────────────────────
 *
 * - Un bucle de redirecciones. `redirigido` se escribe ANTES de navegar y, si
 *   no se puede escribir (sin `sessionStorage`), NO se redirige: es la única
 *   forma de garantizar «como mucho una vez».
 * - Que esto rompa la carga. Si el servidor no contesta en 1,5 s, falla o el
 *   almacenamiento lanza, se sigue como antes de que esto existiera.
 * - Redirigir a un bot. Googlebot rastrea desde EEUU y con `en-US`, así que no
 *   le tocaría nunca; aun así se corta aquí y en el servidor, porque es lo
 *   único de esto que podría costar posiciones.
 *
 * Toda la decisión son funciones PURAS (`idiomaEfectivo`, `destinoRedireccion`)
 * para poder probar la tabla entera sin navegador. El estado y la red están
 * en los bordes: `leerSesion`/`escribirSesion` y `prepararIdiomaDeSesion`.
 */
import { IDIOMAS, IDIOMA_RAIZ, idiomaDeLaRuta, paginaDeLaRuta, urlDe } from './rutasPublicas';

export const CLAVE_SESION = 'mts_idioma_sesion';

/** La misma clave que `LanguageContext.setLanguage` y el detector de i18next. */
export const CLAVE_GUARDADO = 'i18nextLng';

/** Idiomas a los que se redirige solo. Ampliarlo es una decisión de negocio. */
export const IDIOMAS_REDIRIGIBLES = ['pt'];

export const TIMEOUT_GEO_MS = 1500;

/** Mismo espíritu que `qr_app/bot_filter.py`: crawlers, previsualizadores y headless. */
const BOT_UA_RE = /bot\b|bot\/|crawl|spider|slurp|mediapartners|lighthouse|headless|prerender|facebookexternalhit|bingpreview|google-inspectiontool|whatsapp|telegram|embedly|vkshare|preview/i;

/**
 * @typedef {Object} Sesion
 * @property {string|null} pais        ISO-2 del servidor; `''` si no se supo, `null` si no se ha preguntado.
 * @property {string|null} forzado     Idioma que impone la IP (`'pt'`).
 * @property {string|null} elegido     Lo que el usuario escogió a mano en esta sesión.
 * @property {boolean}     redirigido  Ya hubo la redirección automática de esta sesión.
 */

const SESION_VACIA = Object.freeze({ pais: null, forzado: null, elegido: null, redirigido: false });

/** `'pt-PT'` → `'pt'`; `null` si no es uno de los cinco. */
export function normaliza(valor) {
  if (typeof valor !== 'string') return null;
  const codigo = valor.trim().toLowerCase().replace('_', '-').split('-')[0];
  return IDIOMAS.includes(codigo) ? codigo : null;
}

/** El primer idioma del navegador que servimos, o `null`. */
export function idiomaDelNavegador(languages) {
  for (const l of Array.isArray(languages) ? languages : []) {
    const codigo = normaliza(l);
    if (codigo) return codigo;
  }
  return null;
}

export function esBot(userAgent, webdriver) {
  return webdriver === true || BOT_UA_RE.test(userAgent || '');
}

/** `?idioma=es` en la URL: una elección manual, igual que pulsar el selector. */
export function idiomaDeLaQuery(search) {
  try {
    return normaliza(new URLSearchParams(search || '').get('idioma'));
  } catch {
    return null;
  }
}

/**
 * El idioma de una ruta que NO impone idioma (panel, `/register`, `/login`…).
 *
 * El de la cuenta va DETRÁS del navegador a propósito. Este mismo fallo dejó
 * guardados como `es` a clientes alemanes, franceses e ingleses que hoy ven el
 * panel en su idioma gracias al navegador; ponerla delante se lo cambiaría al
 * español de golpe. Así solo decide cuando el navegador no habla ninguno de
 * los nuestros.
 *
 * @param {{ sesion?: Partial<Sesion>|null, guardado?: string|null, navegador?: string|null, cuenta?: string|null }} [entrada]
 * @returns {string}
 */
export function idiomaEfectivo({ sesion, guardado, navegador, cuenta } = {}) {
  return (
    normaliza(sesion?.elegido)
    || normaliza(sesion?.forzado)
    || normaliza(guardado)
    || normaliza(navegador)
    || normaliza(cuenta)
    || IDIOMA_RAIZ
  );
}

/**
 * La URL a la que redirigir, o `null`. Solo desde una página PÚBLICA española
 * de la tabla —nunca las legales, `/redirect`, `/en1/…`, `/join` ni el panel—,
 * una vez por sesión y nunca si el usuario ya eligió.
 *
 * Conserva `search` y `hash`: UTM, `gclid` y `?cta=` tienen que llegar a la
 * página de destino o la visita deja de estar atribuida.
 *
 * @param {{ pathname?: string, search?: string, hash?: string, sesion?: Partial<Sesion>|null,
 *           guardado?: string|null, navegador?: string|null, bot?: boolean }} [entrada]
 * @returns {string|null}
 */
export function destinoRedireccion({ pathname, search = '', hash = '', sesion, guardado, navegador, bot } = {}) {
  if (!sesion || sesion.redirigido || sesion.elegido || bot) return null;
  if (idiomaDeLaRuta(pathname) !== IDIOMA_RAIZ) return null;
  const pagina = paginaDeLaRuta(pathname);
  if (!pagina) return null;

  let idioma = null;
  const forzado = normaliza(sesion.forzado);
  const nav = normaliza(navegador);
  if (forzado && IDIOMAS_REDIRIGIBLES.includes(forzado)) {
    idioma = forzado;
  } else if (nav && IDIOMAS_REDIRIGIBLES.includes(nav)) {
    // El navegador solo manda si no hay OTRA elección guardada: quien escogió
    // español a mano otro día no tiene por qué volver a hacerlo.
    const g = normaliza(guardado);
    if (!g || g === nav) idioma = nav;
  }
  if (!idioma || idioma === IDIOMA_RAIZ) return null;

  const url = urlDe(pagina, idioma);
  if (!url) return null;
  return `${url}${search}${hash}`;
}

// ── Estado de sesión ────────────────────────────────────────────────────────

function sesionValida(bruta) {
  const s = bruta && typeof bruta === 'object' ? bruta : {};
  return {
    pais: typeof s.pais === 'string' ? s.pais : null,
    forzado: normaliza(s.forzado),
    elegido: normaliza(s.elegido),
    redirigido: s.redirigido === true,
  };
}

function almacenSesion() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    // Con las cookies bloqueadas, ACCEDER a `sessionStorage` ya lanza.
    return null;
  }
}

/** La sesión guardada, siempre con forma válida. Sin almacenamiento, vacía. */
export function leerSesion(almacen = almacenSesion()) {
  try {
    const crudo = almacen?.getItem(CLAVE_SESION);
    return sesionValida(crudo ? JSON.parse(crudo) : null);
  } catch {
    return { ...SESION_VACIA };
  }
}

/**
 * Mezcla `cambios` en la sesión y devuelve la nueva. `null` si no se pudo
 * guardar: quien llama decide qué significa eso (para redirigir, «no»).
 */
export function escribirSesion(cambios, almacen = almacenSesion()) {
  const nueva = sesionValida({ ...leerSesion(almacen), ...cambios });
  try {
    if (!almacen) return null;
    almacen.setItem(CLAVE_SESION, JSON.stringify(nueva));
    return nueva;
  } catch {
    return null;
  }
}

/** Lo que el usuario eligió a mano otro día (`LanguageContext.setLanguage`). */
export function leerGuardado() {
  try {
    return normaliza(window.localStorage.getItem(CLAVE_GUARDADO));
  } catch {
    return null;
  }
}

function navegadorActual() {
  if (typeof navigator === 'undefined') return null;
  return idiomaDelNavegador(navigator.languages?.length ? navigator.languages : [navigator.language]);
}

/**
 * `idiomaEfectivo` con el estado real del navegador. Para `IdiomaSegunRuta`.
 *
 * @param {{ cuenta?: string|null }} [entrada]
 */
export function idiomaEfectivoActual({ cuenta } = {}) {
  return idiomaEfectivo({
    sesion: leerSesion(),
    guardado: leerGuardado(),
    navegador: navegadorActual(),
    cuenta,
  });
}

/** Marca una elección manual para el resto de la sesión. */
export function marcarElegido(idioma) {
  const codigo = normaliza(idioma);
  if (codigo) escribirSesion({ elegido: codigo });
}

// ── Red ─────────────────────────────────────────────────────────────────────

/**
 * `{country, forcedLanguage}` del servidor, o `null` si no contesta a tiempo o
 * contesta algo raro. Nunca lanza.
 */
export async function pideGeo(http, timeoutMs = TIMEOUT_GEO_MS) {
  let reloj;
  try {
    const limite = new Promise((resolve) => { reloj = setTimeout(() => resolve(null), timeoutMs); });
    const peticion = http.get('/public/geo/', { timeout: timeoutMs }).then((r) => r?.data ?? null);
    const datos = await Promise.race([peticion.catch(() => null), limite]);
    if (!datos || typeof datos !== 'object') return null;
    return {
      country: typeof datos.country === 'string' ? datos.country : '',
      forcedLanguage: normaliza(datos.forcedLanguage),
    };
  } catch {
    return null;
  } finally {
    clearTimeout(reloj);
  }
}

// ── Arranque ────────────────────────────────────────────────────────────────

/**
 * Lo que corre UNA vez al cargar, antes de montar React (`main.jsx`).
 *
 * Devuelve `{ redirigiendo }`: si es `true` ya se ha pedido la navegación y no
 * hay que montar nada. No lanza nunca: cualquier fallo es `{ redirigiendo: false }`
 * y la página sigue como siempre.
 *
 * @param {{ i18n?: any, http?: any, ventana?: any }} [entrada]
 * @returns {Promise<{ redirigiendo: boolean }>}
 */
export async function prepararIdiomaDeSesion({ i18n, http, ventana = window } = {}) {
  try {
    const { pathname, search, hash } = ventana.location;

    let sesion = leerSesion();
    const deQuery = idiomaDeLaQuery(search);
    if (deQuery) sesion = escribirSesion({ elegido: deQuery }) ?? { ...sesion, elegido: deQuery };

    // Una pregunta por sesión. Si falla se apunta igual (país vacío): no se
    // reintenta en cada navegación ni se retrasa cada carga 1,5 s.
    if (sesion.pais === null) {
      const geo = await pideGeo(http);
      sesion = escribirSesion({ pais: geo?.country ?? '', forzado: geo?.forcedLanguage ?? null })
        ?? { ...sesion, pais: geo?.country ?? '', forzado: geo?.forcedLanguage ?? null };
    }

    const guardado = leerGuardado();
    const navegador = navegadorActual();
    const destino = destinoRedireccion({
      pathname, search, hash, sesion, guardado, navegador,
      bot: esBot(ventana.navigator?.userAgent, ventana.navigator?.webdriver),
    });
    // Antibucle: sin poder apuntar `redirigido`, no se redirige.
    if (destino && escribirSesion({ redirigido: true })?.redirigido) {
      ventana.location.replace(destino);
      return { redirigiendo: true };
    }

    // En las rutas que no imponen idioma, el de la sesión. Las que sí lo
    // imponen ya las ha resuelto el detector `rutaPublica` de `i18n.js`.
    if (!idiomaDeLaRuta(pathname)) {
      const idioma = idiomaEfectivo({ sesion, guardado, navegador });
      if (idioma !== i18n.resolvedLanguage) await i18n.changeLanguage(idioma);
    }
    return { redirigiendo: false };
  } catch (e) {
    console.warn('[idiomaSesion] se sigue sin idioma de sesión', e);
    return { redirigiendo: false };
  }
}
